/**
 * «Проверка фибо» — сетки по диапазонам и их исходы на истории.
 *
 * Метод владельца: хай и лой диапазона, сетка 0 — 1 — 1.414 — 1.618;
 * пробит лой — цель 1.414 снизу, пробит хай — сверху; от зоны 1.414–1.618 —
 * разворот к 1.0. Индикатор сам находит диапазоны, рисует текущую сетку
 * с ценами уровней и по каждой прошлой — чем кончилось: дошла ли цена до
 * 1.414, вернулась ли от неё к 1.0. Табло считает доли за выбранный
 * период и ставит рядом шанс случайного графика при тех же расстояниях:
 * так видно, даёт ли уровень перевес на этой монете или до цели доводит
 * геометрия сетки (до близкой цели доходят чаще, до далёкой — реже).
 *
 * Расчёт — `fiboGrids` из общих правил: те же сетки будят оповещениями.
 */

import { FIBO_REVERSE_CHANCE, FIBO_REVERSE_STOP, FIBO_SWINGS, fiboGrids, fiboLevel, fiboStats, fiboTarget, type FiboGrid, type FiboSwing } from '@specter/rules';
import type { Candle } from '../state/store.js';
import type { IndicatorShape, ReadoutLegendItem, ReadoutRow } from './overlays.js';
import { boolParam, selectParam, type IndicatorContext, type IndicatorDefinition, type IndicatorParams } from './types.js';
import { TABLE_PARAM, priceWords, situationRows, type Situation } from './explain.js';
import { deepOf } from './imbalances.js';

const FIB = '#C9B8FF';
const HIT = '#2FE6A6';
const MISS = '#FF4D7A';
const BACK = '#4FE3FF';
const DAY = 86_400_000;

const SWING_WORDS: Record<FiboSwing, string> = { small: 'мелкие', medium: 'средние', large: 'крупные' };

let memo: { key: string; bars: readonly Candle[]; grids: readonly FiboGrid[] } | null = null;

function gridsOf(chart: readonly Candle[], p: IndicatorParams, context: IndicatorContext): { grids: readonly FiboGrid[]; bars: readonly Candle[] } {
  const bars = deepOf(chart, context);
  const swing = swingOf(p);
  const key = `${context.instrument}|${context.timeframe}|${swing}`;
  if (memo && memo.key === key && memo.bars === bars) return memo;
  // Свеча, которая ещё набирается, пробоем не считается: закрытия нет.
  const closed = context.livePartial ? bars.slice(0, -1) : bars;
  memo = { key, bars, grids: fiboGrids(closed, FIBO_SWINGS[swing]) };
  return memo;
}

function swingOf(p: IndicatorParams): FiboSwing {
  const value = selectParam(p, 'swing', 'medium');
  return value === 'small' || value === 'large' ? value : 'medium';
}

function periodDays(p: IndicatorParams): number {
  const value = Number(selectParam(p, 'period', '90'));
  return value === 30 || value === 365 ? value : 90;
}

const level = (grid: FiboGrid, x: number): string => `${x === 0 ? '0' : x === 1 ? '1' : x.toFixed(3)} · ${priceWords(fiboLevel(grid, x))}`;

function shapesOf(chart: readonly Candle[], p: IndicatorParams, context: IndicatorContext): IndicatorShape[] {
  const last = chart.at(-1);
  if (!last) return [];
  const { grids } = gridsOf(chart, p, context);
  if (grids.length === 0) return [];
  const floor = chart[0]!.time;
  const since = last.time - periodDays(p) * DAY;
  const current = grids[grids.length - 1]!;
  const shapes: IndicatorShape[] = [];

  // История: зона 1.414–1.618 с цветом исхода, пробитая граница и точка исхода.
  if (boolParam(p, 'history', true)) {
    for (const grid of grids) {
      if (grid === current || !grid.broken || grid.broken.at < Math.max(floor, since)) continue;
      const color = grid.outcome === 'hit' ? HIT : grid.outcome === 'miss' ? MISS : FIB;
      const a = fiboLevel(grid, 1.414);
      const b = fiboLevel(grid, 1.618);
      shapes.push({
        kind: 'zone',
        from: grid.broken.at,
        to: grid.reverseAt ?? grid.outcomeAt ?? null,
        upper: Math.max(a, b),
        lower: Math.min(a, b),
        tone: grid.broken.side > 0 ? 'up' : 'down',
        color,
        midline: false,
        filled: 0,
        weight: 1,
        closed: grid.outcome !== 'wait',
        transparency: 55,
      });
      const start = Math.max(floor, Math.min(grid.high.time, grid.low.time));
      shapes.push({ kind: 'line', from: { time: start, price: fiboLevel(grid, 1) }, to: { time: grid.broken.at, price: fiboLevel(grid, 1) }, tone: 'up', color: FIB, width: 1, dashed: true, alpha: 0.55 });
      if (grid.outcomeAt !== null && grid.outcome !== 'wait') {
        const at = grid.outcome === 'hit' ? fiboLevel(grid, 1.414) : fiboLevel(grid, 0.5);
        shapes.push({ kind: 'dot', time: grid.outcomeAt, price: at, tone: grid.outcome === 'hit' ? 'up' : 'down', color, radius: 5 });
      }
      if (grid.reverse === 'back' && grid.outcomeAt !== null && grid.reverseAt !== null) {
        shapes.push({ kind: 'line', from: { time: grid.outcomeAt, price: fiboLevel(grid, 1.414) }, to: { time: grid.reverseAt, price: fiboLevel(grid, 1) }, tone: 'up', color: BACK, width: 1.6, alpha: 0.9 });
        shapes.push({ kind: 'dot', time: grid.reverseAt, price: fiboLevel(grid, 1), tone: 'up', color: BACK, radius: 3.5 });
      }
    }
  }

  // Текущая сетка — ярко, с ценами уровней у правого края.
  const start = Math.max(floor, Math.min(current.high.time, current.low.time));
  const line = (price: number, color: string, label: string, dashed = false, alpha = 0.95): IndicatorShape => ({
    kind: 'line',
    from: { time: start, price },
    to: { time: last.time, price },
    tone: 'up',
    color,
    width: 1.4,
    dashed,
    alpha,
    label,
  });
  if (!current.broken) {
    shapes.push(line(current.high.price, FIB, `хай · ${priceWords(current.high.price)}`));
    shapes.push(line(current.low.price, FIB, `лой · ${priceWords(current.low.price)}`));
    shapes.push(line(fiboTarget(current, 1, 1.414), HIT, `пробьют вверх — цель ${priceWords(fiboTarget(current, 1, 1.414))}`, true, 0.6));
    shapes.push(line(fiboTarget(current, -1, 1.414), HIT, `пробьют вниз — цель ${priceWords(fiboTarget(current, -1, 1.414))}`, true, 0.6));
    return shapes;
  }
  const a = fiboLevel(current, 1.414);
  const b = fiboLevel(current, 1.618);
  shapes.push({
    kind: 'zone',
    from: current.broken.at,
    to: null,
    upper: Math.max(a, b),
    lower: Math.min(a, b),
    tone: current.broken.side > 0 ? 'up' : 'down',
    color: HIT,
    midline: false,
    filled: 0,
    weight: 2,
    closed: false,
    transparency: 65,
  });
  shapes.push(line(fiboLevel(current, 0), FIB, level(current, 0), true, 0.7));
  shapes.push(line(fiboLevel(current, 1), FIB, level(current, 1)));
  shapes.push(line(a, HIT, level(current, 1.414)));
  shapes.push(line(b, HIT, level(current, 1.618), true));
  if (current.outcomeAt !== null) {
    const color = current.outcome === 'hit' ? HIT : MISS;
    shapes.push({ kind: 'dot', time: current.outcomeAt, price: current.outcome === 'hit' ? a : fiboLevel(current, 0.5), tone: 'up', color, radius: 4.5 });
  }
  return shapes;
}

const share = (part: number, whole: number): string => `${part} из ${whole} · ${Math.round((part / whole) * 100)} %`;

function verdict(real: number, chance: number, what: string): string {
  const diff = Math.round((real - chance) * 100);
  if (diff >= 8) return `${what} заметно чаще случайного графика (на ${diff} пунктов) — на этой монете и таймфрейме уровень даёт перевес.`;
  if (diff <= -8) return `${what} реже случайного графика (на ${-diff} пунктов) — уровень здесь скорее мешает.`;
  return `${what} почти как у случайного графика — до цели цену доводит геометрия сетки, а не сам уровень.`;
}

function readoutOf(chart: readonly Candle[], p: IndicatorParams, context: IndicatorContext): ReadoutRow[] {
  const last = chart.at(-1);
  if (!last) return [];
  const { grids, bars } = gridsOf(chart, p, context);
  if (grids.length === 0) {
    return situationRows({ title: 'Мало истории', tone: 'muted', text: 'Диапазонов пока нет: не хватает свечей с разворотами. Прокрутите график назад или выберите свинги помельче.' });
  }
  const current = grids[grids.length - 1]!;
  const days = periodDays(p);
  const since = last.time - days * DAY;
  const from = Math.max(since, bars[0]?.time ?? since);

  let situation: Situation;
  if (!current.broken) {
    situation = {
      title: 'Ждём пробоя',
      tone: 'muted',
      text: `Диапазон ${priceWords(current.low.price)} – ${priceWords(current.high.price)}. Свеча закроется выше хая — цель по сетке ${priceWords(fiboTarget(current, 1, 1.414))}; ниже лоя — ${priceWords(fiboTarget(current, -1, 1.414))}.`,
    };
  } else {
    const up = current.broken.side > 0;
    const word = up ? 'хай' : 'лой';
    if (current.outcome === 'wait') {
      situation = {
        title: `Пробит ${word} — цель 1.414`,
        tone: up ? 'bull' : 'bear',
        text: `Свеча закрылась ${up ? 'выше' : 'ниже'} ${priceWords(fiboLevel(current, 1))}. Цель по сетке — ${priceWords(fiboLevel(current, 1.414))}. Если цена раньше вернётся к середине диапазона (${priceWords(fiboLevel(current, 0.5))}), пробой не сработал.`,
      };
    } else if (current.outcome === 'miss') {
      situation = { title: 'Пробой не сработал', tone: 'muted', text: `После пробоя ${word === 'хай' ? 'хая' : 'лоя'} цена вернулась к середине диапазона, не дойдя до 1.414. Ждём следующий диапазон.` };
    } else if (current.reverse === 'wait') {
      situation = {
        title: 'В зоне 1.414–1.618',
        tone: 'warn',
        text: `Цена дошла до цели ${priceWords(fiboLevel(current, 1.414))}. По методу отсюда — разворот к 1.0 (${priceWords(fiboLevel(current, 1))}), стоп за 1.618 (${priceWords(fiboLevel(current, FIBO_REVERSE_STOP))}).`,
      };
    } else if (current.reverse === 'back') {
      situation = { title: 'Разворот к 1.0 сработал', tone: up ? 'bear' : 'bull', text: `От 1.414 цена вернулась к 1.0 (${priceWords(fiboLevel(current, 1))}).` };
    } else {
      situation = { title: 'Ушла за 1.618', tone: up ? 'bull' : 'bear', text: `После 1.414 цена ушла дальше 1.618 — разворот не случился, движение продолжилось.` };
    }
  }

  const stats = fiboStats(grids, from);
  const rows: ReadoutRow[] = [...situationRows(situation)];
  rows.push({ label: 'Счёт', value: '', heading: true });
  if (stats.settled === 0) {
    rows.push({ label: 'Пробоев с исходом', value: 'пока нет', tone: 'muted' });
  } else {
    rows.push({ label: 'Дошли до 1.414', value: share(stats.hits, stats.settled) });
    rows.push({ label: 'Случайный график', value: `${Math.round((stats.chance ?? 0) * 100)} %`, tone: 'muted' });
    if (stats.reversals > 0) {
      rows.push({ label: 'От 1.414 к 1.0', value: share(stats.backs, stats.reversals) });
      rows.push({ label: 'Случайный график', value: `${Math.round(FIBO_REVERSE_CHANCE * 100)} %`, tone: 'muted' });
    }
    const words =
      stats.settled < 8
        ? 'Пробоев за период мало — счёт пока ничего не говорит. Возьмите период длиннее или свинги помельче.'
        : `${verdict(stats.hits / stats.settled, stats.chance ?? 0, 'До 1.414 доходит')}${stats.reversals >= 8 ? ` ${verdict(stats.backs / stats.reversals, FIBO_REVERSE_CHANCE, 'Разворот от 1.414 к 1.0 бывает')}` : ''}`;
    rows.push({ label: 'Вывод', value: words, full: true });
  }
  const shownDays = Math.max(1, Math.round((last.time - from) / DAY));
  rows.push({ label: 'Период', value: `${shownDays} ${shownDays % 10 === 1 && shownDays % 100 !== 11 ? 'день' : shownDays % 10 >= 2 && shownDays % 10 <= 4 && (shownDays % 100 < 12 || shownDays % 100 > 14) ? 'дня' : 'дней'} · свинги ${SWING_WORDS[swingOf(p)]}`, tone: 'muted' });
  return rows;
}

const LEGEND: readonly ReadoutLegendItem[] = [
  { sample: 'solid', term: 'Яркая сетка', text: 'текущий диапазон: 0 и 1 — его границы, 1.414 и 1.618 — цель и край зоны разворота, с ценами у правого края.' },
  { sample: 'dashed', term: 'Пунктир «пробьют вверх / вниз»', text: 'пока диапазон не пробит — куда по сетке цель с каждой стороны.' },
  { sample: 'soft', term: 'Зелёная зона и точка', text: 'прошлый пробой дошёл до 1.414.' },
  { sample: 'faded', term: 'Красная зона и точка', text: 'прошлый пробой вернулся к середине диапазона, не дойдя до 1.414.' },
  { sample: 'strong', term: 'Голубая линия', text: 'от 1.414 цена развернулась и вернулась к 1.0.' },
  { sample: 'dashed', term: 'Случайный график', text: 'как часто дошёл бы до той же цели случайный график при тех же расстояниях до цели и до отмены. Реальная доля заметно выше — уровень работает; почти такая же — работает геометрия.' },
];

export const FIBO_CHECK: IndicatorDefinition = {
  id: 'fibo-check',
  name: 'Проверка фибо',
  description:
    'Сетки фибо по диапазонам: пробит хай или лой — цель 1.414, от 1.414–1.618 — разворот к 1.0. ' +
    'На истории видно, чем кончалась каждая сетка, а табло сравнивает счёт со случайным графиком.',
  author: 'specter',
  placement: 'overlay',
  params: [
    {
      key: 'swing',
      label: 'Размер свингов',
      hint: 'Хай и лой диапазона — развороты, выше (ниже) которых столько свечей с каждой стороны: мелкие — 5, средние — 10, крупные — 20. Крупнее — меньше сеток, но по заметным движениям.',
      type: 'select',
      default: 'medium',
      options: [
        { value: 'small', label: 'Мелкие (5)' },
        { value: 'medium', label: 'Средние (10)' },
        { value: 'large', label: 'Крупные (20)' },
      ],
    },
    {
      key: 'period',
      label: 'Период проверки',
      hint: 'За сколько дней считать счёт на табло и показывать прошлые сетки.',
      type: 'select',
      default: '90',
      options: [
        { value: '30', label: '30 дней' },
        { value: '90', label: '90 дней' },
        { value: '365', label: 'Год' },
      ],
    },
    {
      key: 'history',
      label: 'Показывать прошлые сетки',
      hint: 'Зоны 1.414–1.618 прошлых пробоев с цветом исхода. Выключите, если нужна только текущая сетка.',
      type: 'boolean',
      default: true,
    },
    {
      key: 'notifyBreak',
      label: 'Оповещать: пробит хай или лой',
      hint: 'Когда включены уведомления — сообщать о закрытии свечи за границей диапазона и о цели 1.414.',
      type: 'boolean',
      default: true,
    },
    {
      key: 'notifyZone',
      label: 'Оповещать: цена дошла до 1.414',
      hint: 'Когда включены уведомления — сообщать, что цена в зоне разворота 1.414–1.618.',
      type: 'boolean',
      default: true,
    },
    {
      key: 'notifyBack',
      label: 'Оповещать: от 1.414 вернулась к 1.0',
      hint: 'Когда включены уведомления — сообщать, что разворот от зоны сработал.',
      type: 'boolean',
      default: false,
    },
    TABLE_PARAM,
  ],
  outputs: [],
  deepOn: ['5m', '15m', '30m', '1H', '2H', '4H', '1D'],
  warmup: () => 0,
  calc: () => ({}),
  shapes: shapesOf,
  readout: readoutOf,
  legend: LEGEND,
  label: () => 'Проверка фибо',
};
